/**
 * Client-side hints mirroring `sharing.ValidateFolderName`/`ValidateFolderPath`
 * (ADR 0010 section 2, `internal/sharing/path.go`). The server stays
 * authoritative: these are only inline hints while typing, shown before
 * the submit round-trip, and never the only check performed.
 */

export const MAX_FOLDER_DEPTH = 5;
const MAX_NAME_BYTES = 255;

const FORBIDDEN_CHARS = /[<>:"|?*\\/]/;
const RESERVED_NAMES = new Set([
  "CON",
  "PRN",
  "AUX",
  "NUL",
  "COM0",
  "COM1",
  "COM2",
  "COM3",
  "COM4",
  "COM5",
  "COM6",
  "COM7",
  "COM8",
  "COM9",
  "LPT0",
  "LPT1",
  "LPT2",
  "LPT3",
  "LPT4",
  "LPT5",
  "LPT6",
  "LPT7",
  "LPT8",
  "LPT9",
]);

function isReservedName(name: string): boolean {
  const withoutExtension = name.includes(".") ? name.slice(0, name.indexOf(".")) : name;
  return RESERVED_NAMES.has(withoutExtension.toUpperCase());
}

function hasControlOrFormatChar(name: string): boolean {
  for (const ch of name) {
    const codePoint = ch.codePointAt(0)!;
    // A small, practical subset of Unicode Cf (format) code points, the
    // ones a folder name is realistically typed with: zero-width and
    // bidi-control characters. The server's own check
    // (`unicode.IsControl`/`unicode.Cf`) is authoritative; this hint
    // catches the common cases without shipping a full Unicode table.
    const isFormatChar =
      (codePoint >= 0x200b && codePoint <= 0x200f) ||
      (codePoint >= 0x202a && codePoint <= 0x202e) ||
      codePoint === 0xfeff;
    if (isFormatChar) {
      return true;
    }
  }
  // eslint-disable-next-line no-control-regex -- deliberately matching C0/C1 control characters
  return /[\x00-\x1f\x7f-\x9f]/.test(name);
}

/**
 * Validates one folder name segment (not a full path), mirroring
 * `sharing.ValidateFolderName`. Returns a hint message, or null when the
 * name looks valid.
 */
export function folderNameHint(name: string): string | null {
  if (name === "") {
    return "Enter a folder name.";
  }
  if (name === "." || name === "..") {
    return "That name is not allowed.";
  }
  if (name.startsWith(" ") || name.endsWith(" ") || name.startsWith(".") || name.endsWith(".")) {
    return "The name cannot start or end with a space or a dot.";
  }
  if (FORBIDDEN_CHARS.test(name)) {
    return 'The name cannot contain < > : " | ? * \\ or /.';
  }
  if (hasControlOrFormatChar(name)) {
    return "The name contains a character that is not allowed.";
  }
  if (isReservedName(name)) {
    return "That name is reserved by Windows and cannot be used.";
  }
  if (new TextEncoder().encode(name).length > MAX_NAME_BYTES) {
    return "The name is too long.";
  }
  return null;
}

/** The depth a new folder would have once created under `parentPath` (root is depth 0). */
export function depthAt(parentPath: string): number {
  return parentPath === "" ? 1 : parentPath.split("/").length + 1;
}

/** A hint for creating `name` under `parentPath`, including the depth-5 cap (ADR 0010 section 2). */
export function newFolderHint(parentPath: string, name: string): string | null {
  if (depthAt(parentPath) > MAX_FOLDER_DEPTH) {
    return `Folders can be nested ${MAX_FOLDER_DEPTH} levels deep at most.`;
  }
  return folderNameHint(name);
}

export function joinPath(parentPath: string, name: string): string {
  return parentPath === "" ? name : `${parentPath}/${name}`;
}

/** Splits a folder path into its segments, for breadcrumbs; "" (the root) has none. */
export function pathSegments(path: string): string[] {
  return path === "" ? [] : path.split("/");
}
